import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { ArrowLeft, Rocket, Building2, Eye, CheckCircle2, XCircle, Loader2, X, ImageIcon, Sparkles, Layers, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = (ws) => {
  if (ws === 'livraison_finale') return 0xef4444;
  if (ws === 'livraison_beta' || ws === 'validation_client') return 0xD9A05B;
  return 0x22c55e;
};

// Imperative Three.js scene builder
function createScene(container, floors, selectedFloorIdx, selectedLotId, onLotClick) {
  const width = container.clientWidth;
  const height = container.clientHeight;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x09090B);
  scene.fog = new THREE.Fog(0x09090B, 35, 65);

  const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
  camera.position.set(18, 14, 18);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enablePan = true;
  controls.maxPolarAngle = Math.PI / 2.1;
  controls.minDistance = 8;
  controls.maxDistance = 45;
  controls.target.set(0, floors.length * 0.8, 0);
  controls.update();

  // Lights
  const ambient = new THREE.AmbientLight(0xffffff, 0.35);
  scene.add(ambient);
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.7);
  dirLight.position.set(15, 25, 15);
  dirLight.castShadow = true;
  scene.add(dirLight);
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.25);
  fillLight.position.set(-10, 15, -10);
  scene.add(fillLight);
  const goldLight = new THREE.PointLight(0xD9A05B, 0.3, 40);
  goldLight.position.set(0, 20, 0);
  scene.add(goldLight);

  // Ground
  const groundGeo = new THREE.CircleGeometry(25, 64);
  const groundMat = new THREE.MeshStandardMaterial({ color: 0x08080c, roughness: 0.9 });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -floors.length * 1.5 - 0.3;
  ground.receiveShadow = true;
  scene.add(ground);

  const buildingGroup = new THREE.Group();
  buildingGroup.position.y = -floors.length * 1.5;
  scene.add(buildingGroup);

  // Lot mesh tracking for raycasting
  const lotMeshes = [];

  // Build floors
  floors.forEach((floor, fi) => {
    const y = fi * 3.5;
    const isFloorSelected = fi === selectedFloorIdx;
    const floorOpacity = isFloorSelected ? 0.7 : 0.15;

    // Slab
    const slabGeo = new THREE.BoxGeometry(14, 0.15, 10);
    const slabMat = new THREE.MeshStandardMaterial({ color: 0x1a1a2e, transparent: true, opacity: floorOpacity, roughness: 0.8 });
    const slab = new THREE.Mesh(slabGeo, slabMat);
    slab.position.set(0, y - 0.1, 0);
    slab.receiveShadow = true;
    buildingGroup.add(slab);

    // Wireframe
    const wireGeo = new THREE.BoxGeometry(14, 2.6, 10);
    const wireMat = new THREE.MeshBasicMaterial({ color: 0xD9A05B, wireframe: true, transparent: true, opacity: isFloorSelected ? 0.12 : 0.03 });
    const wire = new THREE.Mesh(wireGeo, wireMat);
    wire.position.set(0, y + 1.2, 0);
    buildingGroup.add(wire);

    // Lots
    const lots = floor.lots || [];
    const cols = Math.max(2, Math.ceil(Math.sqrt(lots.length)));
    const rows = Math.ceil(lots.length / cols) || 1;

    lots.forEach((lot, li) => {
      const row = Math.floor(li / cols);
      const col = li % cols;
      const w = 12 / cols;
      const d = 8 / rows;
      const x = -6 + col * w + w / 2 + 0.5;
      const z = -4 + row * d + d / 2 + 0.5;
      const isLotSelected = lot.lot_id === selectedLotId;
      const color = isLotSelected ? 0xD9A05B : statusColor(lot.workflow_status);
      const opacity = isLotSelected ? 0.85 : isFloorSelected ? 0.5 : 0.2;

      // Lot box
      const lotGeo = new THREE.BoxGeometry(w - 0.4, 2.4, d - 0.4);
      const lotMat = new THREE.MeshStandardMaterial({
        color,
        transparent: true,
        opacity,
        emissive: new THREE.Color(color),
        emissiveIntensity: isLotSelected ? 0.4 : 0.05,
        roughness: 0.6,
        metalness: 0.1
      });
      const lotMesh = new THREE.Mesh(lotGeo, lotMat);
      lotMesh.position.set(x, y + 1.2, z);
      lotMesh.castShadow = true;
      lotMesh.userData = { lot, type: 'lot' };
      buildingGroup.add(lotMesh);
      lotMeshes.push(lotMesh);

      // Lot wireframe
      const lotWireGeo = new THREE.BoxGeometry(w - 0.4, 2.4, d - 0.4);
      const lotWireMat = new THREE.MeshBasicMaterial({ color: isLotSelected ? 0xD9A05B : 0xffffff, wireframe: true, transparent: true, opacity: isLotSelected ? 0.5 : 0.08 });
      const lotWire = new THREE.Mesh(lotWireGeo, lotWireMat);
      lotWire.position.copy(lotMesh.position);
      buildingGroup.add(lotWire);
    });
  });

  // Raycasting for lot clicks
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  const handleClick = (event) => {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(lotMeshes);
    if (intersects.length > 0) {
      const lot = intersects[0].object.userData.lot;
      if (lot) onLotClick(lot);
    }
  };
  renderer.domElement.addEventListener('click', handleClick);

  // Hover cursor
  const handleMove = (event) => {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(lotMeshes);
    renderer.domElement.style.cursor = intersects.length > 0 ? 'pointer' : 'default';
  };
  renderer.domElement.addEventListener('mousemove', handleMove);

  // Animate
  let animId;
  const tick = () => {
    animId = requestAnimationFrame(tick);
    buildingGroup.rotation.y += (Math.sin(Date.now() * 0.0002) * 0.06 - buildingGroup.rotation.y) * 0.02;
    controls.update();
    renderer.render(scene, camera);
  };
  tick();

  // Resize
  const handleResize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  };
  window.addEventListener('resize', handleResize);

  // Cleanup function
  return () => {
    cancelAnimationFrame(animId);
    renderer.domElement.removeEventListener('click', handleClick);
    renderer.domElement.removeEventListener('mousemove', handleMove);
    window.removeEventListener('resize', handleResize);
    controls.dispose();
    renderer.dispose();
    if (container.contains(renderer.domElement)) {
      container.removeChild(renderer.domElement);
    }
  };
}

// --- MAIN PAGE ---
const ViewerPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const cleanupRef = useRef(null);
  const [project, setProject] = useState(null);
  const [floors, setFloors] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(0);
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [genJobs, setGenJobs] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [generating, setGenerating] = useState(null); // job_id or null
  const pollingRef = useRef(null);
  // Batch generation
  const [batchPreview, setBatchPreview] = useState(null); // {scope, floor_id, eligible, already_completed, no_plan, ...}
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchForce, setBatchForce] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { total, done, failed, jobIds }
  const batchPollingRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/${projectId}/hub-data`, { withCredentials: true })
      .then(r => {
        setProject(r.data.project);
        setGenJobs(r.data.generation_jobs || []);
        const allFloors = [];
        for (const b of r.data.buildings) {
          for (const f of b.floors || []) allFloors.push({ ...f, buildingName: b.name });
        }
        if (r.data.orphan_lots?.length > 0)
          allFloors.push({ name: 'Autres lots', level: allFloors.length, lots: r.data.orphan_lots });
        if (allFloors.length === 0)
          allFloors.push({ name: 'Aucun étage', level: 0, lots: [] });
        setFloors(allFloors);
      })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleLotClick = useCallback((lot) => {
    setSelectedLot(prev => prev?.lot_id === lot.lot_id ? null : lot);
  }, []);

  // Build/rebuild Three.js scene
  useEffect(() => {
    if (!canvasRef.current || floors.length === 0) return;
    if (cleanupRef.current) cleanupRef.current();
    cleanupRef.current = createScene(canvasRef.current, floors, selectedFloor, selectedLot?.lot_id, handleLotClick);
    return () => { if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; } };
  }, [floors, selectedFloor, selectedLot?.lot_id, handleLotClick]);

  // Cleanup polling on unmount
  useEffect(() => () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (batchPollingRef.current) clearInterval(batchPollingRef.current);
  }, []);

  // Request preview for batch generation (floor or building)
  const openBatchPreview = async (scope) => {
    setBatchLoading(true);
    setBatchForce(false);
    try {
      const body = { scope, preview: true };
      if (scope === 'floor') {
        const fid = floors[selectedFloor]?.floor_id;
        if (!fid) { toast.error("Cet étage n'est pas enregistré en base"); setBatchLoading(false); return; }
        body.floor_id = fid;
      }
      const res = await axios.post(`${API}/viewer/${projectId}/generate-batch`, body, { withCredentials: true });
      setBatchPreview({ ...res.data, scope });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur pré-visualisation batch');
    } finally {
      setBatchLoading(false);
    }
  };

  const confirmBatch = async () => {
    if (!batchPreview) return;
    setBatchLoading(true);
    try {
      const body = {
        scope: batchPreview.scope,
        preview: false,
        force_regenerate: batchForce,
      };
      if (batchPreview.scope === 'floor') body.floor_id = batchPreview.floor_id;
      const res = await axios.post(`${API}/viewer/${projectId}/generate-batch`, body, { withCredentials: true });
      const created = res.data.created || [];
      if (created.length === 0) { toast.info('Aucun lot à générer'); setBatchPreview(null); setBatchLoading(false); return; }
      const jobIds = created.map(c => c.job_id);
      setBatchProgress({ total: jobIds.length, done: 0, failed: 0, jobIds });
      setBatchPreview(null);
      toast.success(`${jobIds.length} génération(s) lancée(s)`);
      startBatchPolling(jobIds);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération batch');
    } finally {
      setBatchLoading(false);
    }
  };

  const startBatchPolling = (jobIds) => {
    if (batchPollingRef.current) clearInterval(batchPollingRef.current);
    batchPollingRef.current = setInterval(async () => {
      try {
        const ids = jobIds.join(',');
        const res = await axios.get(`${API}/viewer/jobs/status?ids=${ids}`, { withCredentials: true });
        const jobs = res.data.jobs || [];
        const done = jobs.filter(j => j.status === 'completed').length;
        const failed = jobs.filter(j => j.status === 'failed').length;
        setBatchProgress(prev => prev ? { ...prev, done, failed } : prev);
        if (done + failed >= jobIds.length) {
          clearInterval(batchPollingRef.current);
          batchPollingRef.current = null;
          // Refresh hub-data to pick up new shoebox URLs
          try {
            const hubRes = await axios.get(`${API}/projects/${projectId}/hub-data`, { withCredentials: true });
            setGenJobs(hubRes.data.generation_jobs || []);
          } catch {/* ignore */}
          toast.success(`Batch terminé : ${done} OK · ${failed} échec(s)`);
          setTimeout(() => setBatchProgress(null), 5000);
        }
      } catch {/* polling error, continue */}
    }, 4000);
  };

  const handleGenerate = async (lot) => {
    try {
      const res = await axios.post(`${API}/viewer/generate/${lot.lot_id}`, {}, { withCredentials: true });
      const jobId = res.data.job_id;
      setGenerating(jobId);
      toast.info('Génération Shoebox lancée...');

      // Poll every 3s
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/viewer/job/${jobId}/status`, { withCredentials: true });
          if (statusRes.data.status === 'completed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setGenerating(null);
            // Refresh genJobs
            const hubRes = await axios.get(`${API}/projects/${projectId}/hub-data`, { withCredentials: true });
            setGenJobs(hubRes.data.generation_jobs || []);
            toast.success('Shoebox générée !');
          } else if (statusRes.data.status === 'failed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setGenerating(null);
            toast.error(statusRes.data.error_message || 'Erreur de génération');
          }
        } catch { /* polling error, ignore */ }
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur génération');
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    await new Promise(r => setTimeout(r, 3000));
    setDeploying(false);
    setDeployed(true);
    toast.success('Mini-Site déployé !');
  };

  const floor = floors[selectedFloor] || { name: '', lots: [] };

  const statusBadge = (lot) => {
    const ws = lot.workflow_status;
    if (ws === 'livraison_finale') return <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3 h-3" />Livré</span>;
    if (ws === 'livraison_beta' || ws === 'validation_client') return <span className="inline-flex items-center gap-1 text-[#D9A05B] text-xs"><Eye className="w-3 h-3" />En cours</span>;
    return <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3 h-3" />Disponible</span>;
  };

  const getShoeboxUrl = (lotId) => {
    const job = genJobs.find(j => j.lot_id === lotId && j.generated_image_path);
    return job ? `${API}/lots/${lotId}/generations/${job.job_id}/image` : null;
  };
  const getPlanUrl = (lot) => lot.selected_plan_file_id ? `${API}/files/${lot.selected_plan_file_id}/download` : null;

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#D9A05B] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="h-screen bg-[#09090B] text-white flex flex-col overflow-hidden" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="viewer-page">
      {/* Header */}
      <div className="h-14 border-b border-white/10 px-6 flex items-center justify-between shrink-0 z-50">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/projects')} className="text-white/40 hover:text-white transition-colors"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Viewer 3D{project ? ` — ${project.name}` : ''}</h1>
        </div>
        {deployed ? (
          <span className="text-xs text-emerald-400 bg-emerald-400/10 px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />En ligne</span>
        ) : (
          <button className="bg-[#D9A05B] text-black font-semibold px-5 py-2 rounded-xl hover:bg-[#C78D46] transition-all shadow-[0_0_15px_rgba(217,160,91,0.3)] text-sm inline-flex items-center gap-2 disabled:opacity-50"
            onClick={handleDeploy} disabled={deploying} data-testid="deploy-btn">
            {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {deploying ? 'Déploiement...' : 'Déployer Mini-Site'}
          </button>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Three.js canvas */}
        <div className="flex-1 relative" data-testid="viewer-3d-canvas">
          <div ref={canvasRef} className="absolute inset-0" />

          {/* Floor indicator */}
          <div className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-2">
            <p className="text-[9px] uppercase tracking-widest text-white/40 font-bold">Niveau</p>
            <p className="text-base font-semibold text-white">{floor.name}</p>
          </div>

          {/* Lot detail panel */}
          {selectedLot && (
            <div className="absolute bottom-4 left-4 right-4 lg:right-[340px] z-10 bg-black/70 backdrop-blur-2xl border border-[#D9A05B]/20 rounded-xl overflow-hidden" data-testid="shoebox-panel">
              <div className="flex items-stretch h-28">
                {getPlanUrl(selectedLot) && (
                  <div className="w-28 shrink-0 border-r border-white/10 bg-white/5 p-1.5">
                    <img src={getPlanUrl(selectedLot)} alt="Plan" crossOrigin="use-credentials" className="w-full h-full object-contain rounded" />
                  </div>
                )}
                {getShoeboxUrl(selectedLot.lot_id) ? (
                  <div className="w-44 shrink-0 border-r border-white/10">
                    <img src={getShoeboxUrl(selectedLot.lot_id)} alt="Shoebox" crossOrigin="use-credentials" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-44 shrink-0 border-r border-white/10 bg-white/[0.02] flex flex-col items-center justify-center gap-2 p-3">
                    {generating ? (
                      <>
                        <Loader2 className="w-6 h-6 text-[#D9A05B] animate-spin" />
                        <p className="text-[9px] text-[#D9A05B] font-medium">Génération...</p>
                      </>
                    ) : (
                      <>
                        <button
                          className="bg-[#D9A05B] text-black text-[10px] font-semibold px-3 py-1.5 rounded-lg hover:bg-[#C78D46] transition-colors flex items-center gap-1.5 shadow-[0_0_10px_rgba(217,160,91,0.3)]"
                          onClick={() => handleGenerate(selectedLot)}
                          data-testid="generate-shoebox-btn"
                        >
                          <Sparkles className="w-3 h-3" />Générer
                        </button>
                        <p className="text-[8px] text-white/20">100 jetons</p>
                      </>
                    )}
                  </div>
                )}
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">{selectedLot.lot_number || selectedLot.name}</h3>
                    <button onClick={() => setSelectedLot(null)} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/40 mt-1">
                    <span className="bg-white/10 px-1.5 py-0.5 rounded text-[10px]">{selectedLot.typology}</span>
                    {selectedLot.surface > 0 && <span>{selectedLot.surface} m²</span>}
                    {statusBadge(selectedLot)}
                  </div>
                  {selectedLot.rooms_config?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedLot.rooms_config.map((r, i) => <span key={i} className="text-[9px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded">{r.room}</span>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!selectedLot && (
            <div className="absolute bottom-4 left-4 z-[5]">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#D9A05B] font-bold mb-0.5">Maquette 3D</p>
              <h2 className="text-xl font-light text-white tracking-tight">{project?.name}</h2>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="w-80 shrink-0 border-l border-white/10 bg-black/40 backdrop-blur-xl flex flex-col" data-testid="viewer-panel">
          <div className="p-3 border-b border-white/10">
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Étages</p>
            <div className="space-y-1">
              {floors.map((f, i) => (
                <button key={i}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-left text-sm ${
                    selectedFloor === i ? 'bg-[#D9A05B]/15 border border-[#D9A05B]/30 text-[#D9A05B]' : 'hover:bg-white/5 border border-transparent text-white/50'
                  }`}
                  onClick={() => { setSelectedFloor(i); setSelectedLot(null); }}
                  data-testid={`floor-btn-${i}`}>
                  <Building2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="font-medium flex-1">{f.name}</span>
                  <span className="text-[10px] text-white/30 font-mono">{f.lots?.length || 0}</span>
                </button>
              ))}
            </div>

            {/* Batch generation actions */}
            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
              <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-bold mb-1.5">Génération batch</p>
              <button
                onClick={() => openBatchPreview('floor')}
                disabled={batchLoading || !!batchProgress || !floor.floor_id}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#D9A05B]/10 hover:bg-[#D9A05B]/20 border border-[#D9A05B]/30 text-[#D9A05B] text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="batch-floor-btn"
                title="Générer toutes les shoeboxes de l'étage sélectionné"
              >
                <Layers className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left">Générer cet étage</span>
                <span className="text-[10px] text-[#D9A05B]/70 font-mono">{floor.lots?.length || 0}</span>
              </button>
              <button
                onClick={() => openBatchPreview('building')}
                disabled={batchLoading || !!batchProgress}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] hover:bg-white/10 border border-white/10 text-white/70 text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="batch-building-btn"
                title="Générer toutes les shoeboxes du bâtiment"
              >
                <Zap className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left">Tout le bâtiment</span>
              </button>

              {/* Progress */}
              {batchProgress && (
                <div className="mt-2 p-2.5 rounded-lg bg-black/50 border border-[#D9A05B]/20" data-testid="batch-progress">
                  <div className="flex items-center justify-between text-[10px] mb-1.5">
                    <span className="text-[#D9A05B] font-semibold flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />En cours</span>
                    <span className="text-white/50 font-mono">{batchProgress.done + batchProgress.failed}/{batchProgress.total}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D9A05B] transition-all" style={{ width: `${((batchProgress.done + batchProgress.failed) / batchProgress.total) * 100}%` }} />
                  </div>
                  {batchProgress.failed > 0 && <p className="text-[9px] text-red-400 mt-1">{batchProgress.failed} échec(s)</p>}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">Lots — {floor.name}</p>
            <div className="space-y-1">
              {(floor.lots || []).map((lot, i) => {
                const isActive = lot.lot_id === selectedLot?.lot_id;
                const hasShoebox = !!getShoeboxUrl(lot.lot_id);
                return (
                  <div key={lot.lot_id || i}
                    className={`rounded-lg p-2.5 cursor-pointer transition-all border ${isActive ? 'bg-[#D9A05B]/10 border-[#D9A05B]/30' : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05]'}`}
                    onClick={() => setSelectedLot(isActive ? null : lot)}
                    data-testid={`viewer-lot-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          generating && selectedLot?.lot_id === lot.lot_id ? 'bg-[#D9A05B] animate-pulse shadow-[0_0_5px_rgba(217,160,91,0.5)]' :
                          hasShoebox ? 'bg-emerald-400 shadow-[0_0_5px_rgba(16,185,129,0.5)]' : 'bg-white/15'
                        }`} />
                        <div>
                          <p className="text-xs font-medium text-white">{lot.lot_number || lot.name}</p>
                          <p className="text-[10px] text-white/25">{lot.typology}{lot.surface > 0 ? ` · ${lot.surface}m²` : ''}</p>
                        </div>
                      </div>
                      {statusBadge(lot)}
                    </div>
                  </div>
                );
              })}
              {(floor.lots || []).length === 0 && <p className="text-xs text-white/20 text-center py-8">Aucun lot</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Batch confirmation modal */}
      {batchPreview && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="batch-modal">
          <div className="bg-[#111113] border border-[#D9A05B]/30 rounded-2xl max-w-lg w-full overflow-hidden shadow-[0_0_60px_rgba(217,160,91,0.15)]">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-[0.25em] text-[#D9A05B] font-bold">Confirmer la génération</p>
                <h2 className="text-lg font-semibold text-white mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {batchPreview.scope === 'floor' ? `Étage — ${floor.name}` : 'Bâtiment complet'}
                </h2>
              </div>
              <button onClick={() => setBatchPreview(null)} className="text-white/40 hover:text-white" data-testid="batch-modal-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-[9px] uppercase tracking-wider text-emerald-400/70 font-bold">À générer</p>
                  <p className="text-2xl font-bold text-emerald-400 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{batchPreview.eligible.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#D9A05B]/10 border border-[#D9A05B]/20">
                  <p className="text-[9px] uppercase tracking-wider text-[#D9A05B]/80 font-bold">Déjà OK</p>
                  <p className="text-2xl font-bold text-[#D9A05B] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{batchPreview.already_completed.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Sans plan</p>
                  <p className="text-2xl font-bold text-white/60 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{batchPreview.no_plan.length}</p>
                </div>
              </div>

              {batchPreview.already_completed.length > 0 && (
                <label className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 cursor-pointer hover:bg-white/[0.05] transition-colors" data-testid="batch-force-toggle">
                  <input
                    type="checkbox"
                    checked={batchForce}
                    onChange={(e) => setBatchForce(e.target.checked)}
                    className="w-4 h-4 accent-[#D9A05B]"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-white font-medium">Régénérer les lots déjà générés</p>
                    <p className="text-[11px] text-white/40 mt-0.5">Écrase les shoeboxes existantes ({batchPreview.already_completed.length} lots)</p>
                  </div>
                </label>
              )}

              {batchPreview.no_plan.length > 0 && (
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <p className="text-[11px] text-red-400/90">
                    <span className="font-semibold">{batchPreview.no_plan.length} lot(s)</span> sans plan assigné seront ignorés.
                  </p>
                </div>
              )}

              <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Coût total</p>
                  <p className="text-xl font-bold text-white mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {(batchPreview.eligible.length + (batchForce ? batchPreview.already_completed.length : 0)) * 100}
                    <span className="text-xs text-white/50 ml-1 font-normal">jetons</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Solde</p>
                  <p className="text-xl font-bold text-white mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{batchPreview.tokens_available}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-black/40 border-t border-white/10 flex items-center gap-3 justify-end">
              <button
                onClick={() => setBatchPreview(null)}
                className="px-4 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm font-medium transition-all"
                data-testid="batch-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={confirmBatch}
                disabled={batchLoading || (batchPreview.eligible.length + (batchForce ? batchPreview.already_completed.length : 0)) === 0}
                className="px-5 py-2 rounded-lg bg-[#D9A05B] text-black text-sm font-semibold hover:bg-[#C78D46] transition-all shadow-[0_0_15px_rgba(217,160,91,0.3)] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="batch-confirm-btn"
              >
                {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Lancer la génération
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewerPage;
